'use client';
import { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Link from '@/components/ui/Link';

interface Product {
    id: number;
    image: string;
    alt: string;
    name: string;
    price: string;
    originalPrice?: string;
    rating: number;
    reviews: number;
    badge?: string;
    category: string;
    imgClass: string;
}

const products: Product[] = [
    { id: 1, image: '/images/img_image.webp', alt: 'Yves Saint classic shirt in neutral tone', name: 'Yves Saint Classic', price: '₹39.99', originalPrice: '₹59.99', rating: 5, reviews: 128, badge: 'New', category: 'Men', imgClass: 'object-cover object-[center_top]' },
    { id: 2, image: '/images/img_image_382x336.webp', alt: 'Yves Saint premium sunglasses', name: 'Yves Saint Sunglasses', price: '₹49.99', rating: 4, reviews: 96, category: 'Bags', imgClass: 'object-cover object-center' },
    { id: 3, image: '/images/img_image_4.webp', alt: 'Yves Saint summer collection dress', name: 'Yves Saint Summer', price: '₹34.99', originalPrice: '₹54.99', rating: 5, reviews: 210, badge: 'Sale', category: 'Women', imgClass: 'object-cover object-[center_top]' },
    { id: 4, image: '/images/img_image_5.webp', alt: 'Yves Saint casual shoulder bag', name: 'Yves Saint Bag', price: '₹29.99', rating: 4, reviews: 74, category: 'Bags', imgClass: 'object-cover object-center' },
    { id: 5, image: '/images/img_image_6.webp', alt: 'Yves Saint luxury evening wear', name: 'Yves Saint Luxe', price: '₹69.99', rating: 5, reviews: 182, badge: 'Trending', category: 'Women', imgClass: 'object-cover object-[center_top]' },
    { id: 6, image: '/images/img_rectangle_9.webp', alt: 'Yves Saint kids apparel collection', name: 'Yves Saint Kids', price: '₹24.99', rating: 4, reviews: 55, category: 'Kids', imgClass: 'object-cover object-[center_top]' },
    { id: 7, image: '/images/img_image_2.webp', alt: 'Yves Saint sport shoes and outfit', name: 'Yves Saint Sport', price: '₹59.99', originalPrice: '₹79.99', rating: 5, reviews: 143, badge: 'Sale', category: 'Shoes', imgClass: 'object-cover object-[right_top]' },
    { id: 8, image: '/images/img_image_3.webp', alt: 'Yves Saint casual jacket', name: 'Yves Saint Jacket', price: '₹89.99', rating: 5, reviews: 67, badge: 'New', category: 'Men', imgClass: 'object-cover object-[right_top]' },
];

const categories = ['All', 'Men', 'Women', 'Kids', 'Shoes', 'Bags'];
const sortOptions = ['Newest', 'Price: Low to High', 'Price: High to Low', 'Most Popular'];

const StarRating = ({ rating }: { rating: number }) => (
    <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
            <img key={i} src="/images/img_frame_amber_500.svg" alt="" className="w-[14px] h-[14px]" />
        ))}
    </div>
);

export default function ShopPage() {
    const [activeCategory, setActiveCategory] = useState('All');
    const [sortBy, setSortBy] = useState('Newest');
    const [searchQuery, setSearchQuery] = useState('');
    const [wishlist, setWishlist] = useState<number[]>([]);
    const [addedCart, setAddedCart] = useState<number[]>([]);

    const toggleWishlist = (id: number) => {
        setWishlist(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const handleAddToCart = (id: number) => {
        if (!addedCart.includes(id)) {
            setAddedCart(prev => [...prev, id]);
            setTimeout(() => {
                setAddedCart(prev => prev.filter(item => item !== id));
            }, 2000);
        }
    };

    // Helper to get raw numeric price
    const getNumericPrice = (priceStr: string) => {
        return parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
    };

    // 1. Filter by category & search query
    let filtered = products.filter(product => {
        const matchesCategory = activeCategory === 'All' || product.category === activeCategory;
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.category.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // 2. Sort results
    filtered = [...filtered].sort((a, b) => {
        if (sortBy === 'Price: Low to High') {
            return getNumericPrice(a.price) - getNumericPrice(b.price);
        }
        if (sortBy === 'Price: High to Low') {
            return getNumericPrice(b.price) - getNumericPrice(a.price);
        }
        if (sortBy === 'Most Popular') {
            return b.reviews - a.reviews;
        }
        // Default: 'Newest' (by id descending)
        return b.id - a.id;
    });

    return (
        <>
            <Header />
            <main className="w-full bg-background-tertiary min-h-screen">
                {/* Page Banner */}
                <div className="w-full bg-background-secondary py-10 sm:py-14 lg:py-[60px]">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-sm text-text-opacity-60">
                                <Link href="/" ariaLabel="Home" className="hover:text-primary-brand transition-colors">Home</Link>
                                <span>/</span>
                                <span className="text-text-primary font-medium">Shop</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-8xl text-text-primary font-semibold leading-tight">
                                Shop All
                            </h1>
                            <p className="text-lg sm:text-xl text-text-opacity-60 font-normal mt-1">
                                Discover our full collection of premium fashion
                            </p>
                        </div>
                    </div>
                </div>

                <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] py-10 sm:py-12 lg:py-[60px]">
                    {/* Filters, Search & Sort Row */}
                    <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-8 lg:mb-10">
                        {/* Category Tabs */}
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    className={`px-4 sm:px-5 py-2 rounded-full text-sm sm:text-base font-medium transition-all duration-200 border ${activeCategory === cat
                                        ? 'bg-primary-brand text-white border-primary-brand shadow-sm'
                                        : 'bg-white text-text-primary border-border-light hover:border-primary-brand hover:text-primary-brand'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>

                        {/* Search & Sort Controls */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            {/* Instant Search Bar */}
                            <div className="relative flex-1 sm:w-64">
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full border border-border-light rounded-lg pl-9 pr-8 py-2 text-sm text-text-primary bg-white focus:outline-none focus:border-primary-brand shadow-sm"
                                />
                                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs bg-gray-100 rounded-full w-4 h-4 flex items-center justify-center"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            {/* Sort Dropdown */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-text-opacity-60 whitespace-nowrap">Sort by:</span>
                                <select
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}
                                    className="border border-border-light rounded-lg px-3 py-2 text-sm font-medium text-text-primary bg-white focus:outline-none focus:border-primary-brand shadow-sm cursor-pointer"
                                >
                                    {sortOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Results Count & Active Filters Indicator */}
                    <div className="flex items-center justify-between mb-6">
                        <p className="text-sm text-text-opacity-60">
                            Showing <span className="font-semibold text-text-primary">{filtered.length}</span> of {products.length} products
                        </p>
                        {(activeCategory !== 'All' || searchQuery !== '' || sortBy !== 'Newest') && (
                            <button
                                onClick={() => {
                                    setActiveCategory('All');
                                    setSearchQuery('');
                                    setSortBy('Newest');
                                }}
                                className="text-xs text-primary-brand font-semibold hover:underline"
                            >
                                Reset All Filters ↺
                            </button>
                        )}
                    </div>

                    {/* Products Grid or Empty State */}
                    {filtered.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
                            {filtered.map(product => (
                                <div key={product.id} className="group flex flex-col gap-3 bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-border-light/60 shadow-sm hover:shadow-lg transition-all duration-300">
                                    {/* Image Container */}
                                    <div className="relative overflow-hidden bg-[#f4f4f6] aspect-[4/5] w-full flex items-center justify-center">
                                        <img
                                            src={product.image}
                                            alt={product.alt}
                                            className={`w-full h-full ${product.imgClass}`}
                                        />
                                        {product.badge && (
                                            <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white ${product.badge === 'Sale' ? 'bg-red-500' :
                                                product.badge === 'New' ? 'bg-primary-brand' : 'bg-secondary-dark'
                                                }`}>
                                                {product.badge}
                                            </span>
                                        )}
                                        <button
                                            onClick={() => toggleWishlist(product.id)}
                                            className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 active:scale-95 cursor-pointer"
                                            aria-label="Add to wishlist"
                                        >
                                            <svg
                                                className={`w-4 h-4 transition-colors ${wishlist.includes(product.id) ? 'fill-red-500 stroke-red-500' : 'fill-none stroke-gray-700'}`}
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.684a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="flex flex-col gap-2 px-4 pb-5 flex-1 justify-between">
                                        <div className="flex flex-col gap-1.5">
                                            <h3 className="text-base sm:text-lg font-semibold text-text-primary line-clamp-1 group-hover:text-primary-brand transition-colors">{product.name}</h3>
                                            <div className="flex items-center gap-2">
                                                <StarRating rating={product.rating} />
                                                <span className="text-xs text-text-opacity-60">({product.reviews})</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-base sm:text-lg font-bold text-primary-accent">{product.price}</span>
                                                {product.originalPrice && (
                                                    <span className="text-sm text-text-opacity-60 line-through">{product.originalPrice}</span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleAddToCart(product.id)}
                                            className={`mt-3 w-full py-2.5 px-4 text-sm font-semibold rounded-lg transition-all duration-200 ${addedCart.includes(product.id)
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-primary-brand text-white hover:bg-primary-brand-dark active:scale-[0.98]'
                                                }`}
                                        >
                                            {addedCart.includes(product.id) ? 'Added to Cart ✓' : 'Add to Cart'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="w-full py-20 text-center flex flex-col items-center justify-center bg-white rounded-2xl border border-border-light p-8">
                            <span className="text-4xl mb-3">🔍</span>
                            <h3 className="text-xl font-bold text-text-primary">No products found</h3>
                            <p className="text-text-opacity-60 text-sm mt-1 max-w-md">No products match your current search or category filter. Try clearing filters to view all styles.</p>
                            <button
                                onClick={() => {
                                    setActiveCategory('All');
                                    setSearchQuery('');
                                    setSortBy('Newest');
                                }}
                                className="mt-5 px-6 py-2.5 bg-primary-brand text-white text-sm font-semibold rounded-full hover:bg-primary-brand-dark transition-colors"
                            >
                                Reset All Filters
                            </button>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
}
